import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { EyeSlash, Eye, ExclamationCircleFill } from "react-bootstrap-icons";
import { Form, Spinner } from "react-bootstrap";
import { loginUser, clearError } from "./slice";
import "./login.css";
import {
  validateEmail,
  validatePassword,
} from "../../../utils/helperFunctions/inputValidations";

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Add debugging
  const loginState = useSelector((state) => {
    return state.login;
  });

  // Use safe destructuring with defaults
  const {
    isLoading = false,
    error = null,
    token = null,
    isCheckingVerification = false,
    verificationError = null,
  } = loginState || {};

  const [formData, setFormData] = useState({
    email: localStorage.getItem("rememberedEmail") || "",
    password: "",
    rememberMe: !!localStorage.getItem("rememberedEmail"),
  });
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Clear auth errors when unmounting
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Handle navigation after successful login
  useEffect(() => {
    if (token) {
      try {
        // Check if there's a returnUrl from failed memberToken validation
        const returnUrl = localStorage.getItem("returnUrl");
        console.log("Login: Token detected, checking returnUrl:", returnUrl);
        
        if (returnUrl) {
          console.log("Login: Found returnUrl, navigating to:", returnUrl);
          // Try to clear the returnUrl, but navigate even if clearing fails
          try {
            localStorage.removeItem("returnUrl");
            console.log("Login: Successfully cleared returnUrl");
          } catch (removeError) {
            console.log("Login: Failed to clear returnUrl, continuing anyway");
            // Ignore removeItem errors, still navigate to returnUrl
          }
          navigate(returnUrl);
        } else {
          console.log("Login: No returnUrl found, navigating to default route");
          // Default navigation
          navigate("/");
        }
      } catch (error) {
        console.log("Login: Error accessing localStorage, using default navigation");
        // Handle localStorage getItem errors gracefully
        navigate("/");
      }
    }
  }, [token, navigate]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear validation errors when typing
    if (name === "email") setEmailError("");
    if (name === "password") setPasswordError("");
    if (error) dispatch(clearError());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let isError = false;

    // Validate email and password
    if (!validateEmail(formData.email)) {
      setEmailError("Invalid email");
      isError = true;
    }
    if (!validatePassword(formData.password)) {
      setPasswordError("Invalid password");
      isError = true;
    }
    if (isError) return;

    // Dispatch login action (which now includes email verification check)
    const result = await dispatch(
      loginUser({
        email: formData.email,
        password: formData.password,
      })
    );

    // Check if login failed due to unverified email
    if (result.error && result.payload?.type === "EMAIL_NOT_VERIFIED") {
      // Store email for verification page and redirect
      sessionStorage.setItem("verificationEmail", formData.email);
      navigate("/verify-email");
      return;
    }

    if (!result.error) {
      // Handle remember me
      if (formData.rememberMe) {
        localStorage.setItem("rememberedEmail", formData.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Determine if we should show loading state (either checking verification or logging in)
  const isLoadingState = isLoading || isCheckingVerification;

  // Determine which error to show
  const displayError = error || verificationError;

  return (
    <div className="login-container">
      <div className="login-content">
        <div className="login-form-wrapper">
          <h1 className="page-title">Welcome</h1>
          <Form className="login-form" onSubmit={handleSubmit} noValidate>
            {displayError && (
              <div className="login-error">
                <ExclamationCircleFill color="#842029" />
                <div>
                  {typeof displayError === "object" && displayError.message
                    ? displayError.message
                    : displayError}
                </div>
              </div>
            )}
            <Form.Group className="form-group">
              <Form.Label>Email</Form.Label>

              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                isInvalid={!!emailError}
                required
              />
              <Form.Control.Feedback type="invalid">
                {emailError}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="form-group">
              <Form.Label>Password</Form.Label>
              <div className="password-input-wrapper">
                <Form.Control
                  type="text"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  isInvalid={!!passwordError}
                  style={{
                    WebkitTextSecurity: showPassword ? "none" : "disc",
                    MozTextSecurity: showPassword ? "none" : "disc",
                    textSecurity: showPassword ? "none" : "disc",
                  }}
                  required
                />
                <span
                  className="password-toggle-icon"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? <Eye size={16} /> : <EyeSlash size={16} />}
                </span>
                <Form.Control.Feedback type="invalid">
                  {passwordError}
                </Form.Control.Feedback>
              </div>
            </Form.Group>

            <div
              className={`form-options ${
                passwordError ? "has-password-error" : ""
              }`}
            >
              <Form.Group>
                <Form.Label className="remember-me mb-0">
                  <Form.Check
                    type="checkbox"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                  />
                  Remember me
                </Form.Label>
              </Form.Group>
              <Link
                to="/reset-password"
                className="text-button-link text-small"
              >
                Forgot password
              </Link>
            </div>

            <button
              type="submit"
              className="primary-action-button"
              disabled={isLoadingState}
            >
              {isLoadingState ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="loading-button-spinner"
                />
              ) : (
                "Log in"
              )}
            </button>

            <div className="register-prompt">
              Don't have an account?{" "}
              <Link to="/create-account" className="text-button-link">
                Register
              </Link>
            </div>
          </Form>
        </div>
      </div>
      <footer className="login-footer">
        <Link
          to="https://www.morph-technologies.com/terms-conditions"
          className="footer-link"
          target="_blank"
        >
          Terms and Conditions
        </Link>
        <span className="footer-separator">•</span>
        <Link
          to="https://www.morph-technologies.com/privacy-policy"
          className="footer-link"
          target="_blank"
        >
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
};

export default Login;
